import React from 'react';
import { ArrowUpRightIcon } from 'lucide-react';
import { SectionHeader } from '../components/ui/SectionHeader';
import { ClosingInvitation } from '../components/sections/ClosingInvitation';
import { LogoMark } from '../components/layout/BrandLockup';
import { SYNEURAL_URL } from '../data/navigation';
const principles = [{
  title: 'Evidence stays visible',
  body: 'Findings, decisions, and records remain linked to their sources, reviewers, and history.'
}, {
  title: 'People make the judgments',
  body: 'Noetivra organizes information and supports review. Professional and institutional judgment stays with people.'
}, {
  title: 'Only what the work needs',
  body: 'Organizations configure the modules relevant to them. Nothing is added for its own sake.'
}, {
  title: 'Familiar by design',
  body: 'Complex systems should feel as approachable as the best everyday software.'
}];
export function About() {
  return <>
      <section className="pb-20 pt-14 md:pb-28 md:pt-20" data-id="element-564" data-mp="1lvm5yn7e0i62">
        <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-565" data-mp="1uh9yldsleyq8">
          <SectionHeader as="h1" title="Making complex institutional systems easier to understand and operate." className="max-w-4xl" data-id="element-566" data-mp="1ujw7bgr7zka1" />
          <div className="mt-10 grid gap-8 text-lg leading-relaxed text-ink-soft md:grid-cols-2 md:gap-14" data-id="element-567" data-mp="23k1crlcus8lk">
            <p data-id="element-568" data-mp="1yi2ayfwrhjqq">
              Institutions hold vast amounts of knowledge and run services that matter to people’s lives. Too often, the
              software behind that work is fragmented, opaque, and hard to trust.
            </p>
            <p data-id="element-569" data-mp="2m8pwi3zlo7vr">
              Noetivra brings that work into one intuitive environment, where people can explore what is known, see where it
              came from, and act on it with confidence.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white/50 py-20 md:py-28" data-id="element-570" data-mp="2w7njv3lahvd6">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 md:px-8 lg:grid-cols-2" data-id="element-571" data-mp="2r8kknzrzqjd4">
          <div data-id="element-572" data-mp="1ov697fvs0j29">
            <h2 className="text-3xl font-semibold tracking-display md:text-4xl" data-id="element-573" data-mp="1ovo4a96pcr8l">Noetivra, by SyNeural.</h2>
            <p className="mt-5 text-lg leading-relaxed text-ink-soft" data-id="element-574" data-mp="1sx1aikdpiwg1">
              SyNeural is the company and the underlying technology provider. It develops the Semantic Knowledge Platform and
              the Human Capability Framework. Noetivra is the software people interact with: the workspaces, dashboards, and
              tools that make those systems usable every day.
            </p>
            <a href={SYNEURAL_URL} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-1 text-[15px] font-medium text-accent hover:text-accent-ink" data-id="element-575" data-mp="2ciw78kgnro7g">
              Visit syneural.com <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-576" data-mp="m8b2op9pgpgw" />
              <span className="sr-only" data-id="element-577" data-mp="r6w6eyo61ra3">(opens in a new tab)</span>
            </a>
          </div>
          <div className="glass rounded-[28px] p-8" data-id="element-578" data-mp="r8pbwan1m5gi">
            <div className="flex items-center gap-3" data-id="element-579" data-mp="jo530ts4jl65">
              <LogoMark className="h-10 w-10" data-id="element-580" data-mp="ewmxrtj2iyfd" />
              <div data-id="element-581" data-mp="evv6uw0f1ovf">
                <p className="font-semibold" data-id="element-582" data-mp="euk0cofl630m">Noetivra</p>
                <p className="text-sm text-ink-muted" data-id="element-583" data-mp="9ws6hovds6ts">The product people use</p>
              </div>
            </div>
            <div className="my-6 ml-5 h-8 border-l border-dashed border-ink/20" aria-hidden="true" data-id="element-584" data-mp="4zs17g6cv83q" />
            <div className="flex items-center gap-3" data-id="element-585" data-mp="12kwjywk71o71">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-sm font-semibold text-ink ring-1 ring-ink/10" data-id="element-586" data-mp="xne5yrrbkis6">
                SN
              </span>
              <div data-id="element-587" data-mp="omgbp16onpms">
                <p className="font-semibold" data-id="element-588" data-mp="omzhjjj26nkg">SyNeural</p>
                <p className="text-sm text-ink-muted" data-id="element-589" data-mp="ooizdekd56oc">The company and underlying technology</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28" data-id="element-590" data-mp="39bw05pifj299">
        <div className="mx-auto max-w-7xl px-5 md:px-8" data-id="element-591" data-mp="113z859u1q2g1">
          <h2 className="text-3xl font-semibold tracking-display md:text-4xl" data-id="element-592" data-mp="112ecthzwqfh4">What guides the product.</h2>
          <dl className="mt-12 grid gap-x-12 sm:grid-cols-2" data-id="element-593" data-mp="15waaejc9e0br">
            {principles.map(p => <div key={p.title} className="border-t border-ink/[0.08] py-7" data-id="element-594" data-mp="r7mf8e6brsmc">
                <dt className="text-lg font-semibold tracking-tight" data-id="element-595" data-mp="w5nyaz3r3rxt">{p.title}</dt>
                <dd className="mt-2 text-[15px] leading-relaxed text-ink-soft" data-id="element-596" data-mp="1ju77dgumk8qy">{p.body}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <ClosingInvitation title="Let’s talk about your organization." body="Tell us about your field and the workflow you want to improve." primary={{
      label: 'Contact us',
      to: '/contact'
    }} secondary={{
      label: 'Explore Demos',
      to: '/demos'
    }} data-id="element-597" data-mp="1atshe46pu5h4" />
    </>;
}